import React from 'react';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import {Card, HiristBanner, BannerInfo} from '@jobseeker/lotus/components';
import {Image} from '@jobseeker/lotus/components/Image';
import Link from 'next/link';
import {Paragraph} from '@jobseeker/lotus/elements';
import {Grid} from '@jobseeker/lotus/foundations';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import {BannerTick} from '@jobseeker/lotus/assets/icons';
import HomePageFooter from '@components/HomeV2/homePageFooter';

type ContactUsProps = {
  isMobile?: boolean;
};

const ContactUs = ({isMobile}: ContactUsProps) => {
  const interceptor: /* eslint-disable @typescript-eslint/no-explicit-any */ any =
    useInterceptor();
  const {imgURL, address = ''} = interceptor.contact;
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const theme = useWrappedTheme();
  return (
    <Box>
      {!isAppIimjobs && (
        <HiristBanner>
          <BannerInfo
            Icon={BannerTick}
            isMobile={!!isMobile}
            subTitle={'Contact Us'}
            title={''}
          />
        </HiristBanner>
      )}
      <Box
        sx={{
          mt: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontWeight: 400,
          padding: !isAppIimjobs
            ? {xxs: '12px 10px', md: '40px 30px', lg: '40px 50px'}
            : {xxs: '24px 0px 0px'},
          marginTop: !isAppIimjobs ? '0px' : '0px',
        }}
      >
        <Card
          variant=""
          heading=""
          sx={{
            pt: {
              md: 6.625,
              xs: 3,
            },
            pb: {
              md: 6.625,
              xs: 0,
            },
            px: {
              md: 3.125,
              xs: 1.5,
            },
            borderRadius: !isAppIimjobs ? '8px' : '15px',
            width: !isAppIimjobs ? '100%' : 'inherit',

            padding: '20px',
            minHeight: !isAppIimjobs
              ? {xxs: 'calc(100vh - 174px)', md: 'calc(100vh - 280px)'}
              : 'auto',
          }}
        >
          {isAppIimjobs && (
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontSize: theme.typography.pxToRem(24),
                fontWeight: 500,
                lineHeight: theme.typography.pxToRem(30),
                textAlign: 'Center',
                color: 'secondary.dark',
                mb: {
                  md: 5,
                  xs: 3,
                },
              }}
            >
              CONTACT US
            </Typography>
          )}
          <Grid
            container
            sx={{
              gap: {
                lg: 8,
                md: 6.5,
              },
              padding: !isAppIimjobs
                ? {md: '0px 0px 0px 40px', lg: '35px 0px 0px 40px'}
                : '0px',
              flexWrap: 'nowrap',
              flexDirection: {xxs: 'column-reverse', sm: 'row'},
            }}
          >
            <Grid
              item
              sx={{
                display: {
                  md: 'initial',
                  xs: !isAppIimjobs ? 'initial' : 'none',
                },
                width: !isAppIimjobs ? {xxs: '60%', md: '20%'} : 'auto',
              }}
            >
              <Image
                src={imgURL}
                alt="Contact us"
                width={169}
                height={137}
                style={{
                  position: 'relative',
                  top: '-9%',
                  width: !isAppIimjobs ? '100%' : '169px',
                  height: !isAppIimjobs ? '100%' : '137px',
                  marginLeft: !isAppIimjobs ? (!isMobile ? '0' : '21.7%') : '0',
                  marginTop: !isAppIimjobs ? (!isMobile ? '0' : '20px') : '0',
                }}
              />
            </Grid>
            <Grid
              item
              sx={{
                marginLeft: !isAppIimjobs ? {md: '50px'} : '0px',
              }}
            >
              <Paragraph
                title=""
                body={
                  <>
                    We would love to hear from you.&nbsp;
                    {!isAppIimjobs && <br />}
                    Please write to{' '}
                    <Link
                      style={{
                        color: isAppIimjobs ? '#49966E' : '#E9630C',
                        fontWeight: isAppIimjobs ? '400px' : 'bolder',
                        textDecoration: 'none',
                      }}
                      href={`mailto:${
                        process.env.NEXT_PUBLIC_APP_DOMAIN === 'iimjobs.com'
                          ? `hello@${process.env.NEXT_PUBLIC_APP_DOMAIN}`
                          : `info@${process.env.NEXT_PUBLIC_APP_DOMAIN}`
                      }`}
                    >
                      {process.env.NEXT_PUBLIC_APP_DOMAIN === 'iimjobs.com'
                        ? `hello@${process.env.NEXT_PUBLIC_APP_DOMAIN}`
                        : `info@${process.env.NEXT_PUBLIC_APP_DOMAIN}`}
                    </Link>{' '}
                    and we&apos;ll get back to you real soon!
                  </>
                }
                style={{
                  ...(!isAppIimjobs && {
                    fontSize: '16px',
                    lineHeight: '24px',
                  }),
                  marginBottom: 3.25,
                  mr: {
                    lg: 6.3,
                    xs: 0.5,
                  },
                }}
              />

              {isAppIimjobs && (
                <Paragraph
                  title="Customer Support"
                  body={
                    <Grid container gap={1}>
                      <Image
                        src="https://static.iimjobs.com/static/images/mail.svg"
                        alt="Contact email"
                        width={15}
                        height={15}
                        style={{marginTop: '7px', marginLeft: '2px'}}
                      />
                      <Link
                        href={`mailto:info@${process.env.NEXT_PUBLIC_APP_DOMAIN}`}
                        style={{
                          marginTop: '4px',
                          textDecoration: 'none',
                          color: 'inherit',
                        }}
                      >
                        info@{process.env.NEXT_PUBLIC_APP_DOMAIN}
                      </Link>
                    </Grid>
                  }
                  style={{
                    marginBottom: 4.875,
                  }}
                />
              )}
              <Paragraph
                title={
                  <Typography
                    fontSize={isAppIimjobs ? '14px' : '16px'}
                    fontWeight={isAppIimjobs ? 500 : 700}
                  >
                    Address {!isAppIimjobs ? ':' : ''}
                  </Typography>
                }
                body={
                  <Grid container gap={1.375} sx={{pt: 0.5}}>
                    {isAppIimjobs && (
                      <Grid item>
                        <Image
                          src="https://static.iimjobs.com/static/images/location.svg"
                          alt="Location"
                          width={15}
                          height={15}
                          style={{marginTop: '3px'}}
                        />
                      </Grid>
                    )}
                    <Grid item>
                      <Paragraph
                        title=""
                        style={{
                          ...(!isAppIimjobs && {
                            fontSize: '16px',
                            lineHeight: '24px',
                          }),
                        }}
                        body={address
                          .split('<br>')
                          .map((line: string, index: number) => (
                            <React.Fragment key={index}>
                              {line}
                              <br />
                            </React.Fragment>
                          ))}
                      />
                    </Grid>
                  </Grid>
                }
                style={{
                  ...(!isAppIimjobs && {
                    fontSize: '16px',
                    lineHeight: '24px',
                  }),
                  mb: {
                    md: 5,
                    xs: 0,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Card>
      </Box>

      {!isAppIimjobs && (
        <Box
          sx={{
            marginBottom: '-64px',
          }}
        >
          <HomePageFooter isTransparent={true} />
        </Box>
      )}
    </Box>
  );
};

export default ContactUs;
